import {Component, Input, OnInit} from '@angular/core';
import {
    BaseStepperFormComponent,
    LoadingService,
    Menu,
    MenuItem,
    MenuSelection,
    MenuSelectionItem,
    MenuSelectionPrice,
    MenuSelectionService,
    MenuService,
    sortAlphabetical,
    sortBySortOrder,
    ToastService
} from 'brewbill-lib';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ModalController} from '@ionic/angular';

@Component({
    selector: 'bb-edit-menu-selection',
    templateUrl: './edit-menu-selection.component.html',
    styleUrls: ['./edit-menu-selection.component.scss'],
})
export class EditMenuSelectionComponent extends BaseStepperFormComponent implements OnInit {
    @Input() menuSelection: MenuSelection;
    @Input() addMenuItem: any;

    protected readonly sortAlphabetical = sortAlphabetical;

    menu: Menu;
    prices: MenuSelectionPrice[] = [];
    selectedItems: MenuSelectionItem[] = [];
    filteredItems: any[] = [];
    informationFormGroup: FormGroup = new FormGroup({});
    options: MenuSelectionItem[];

    constructor(
        modalController: ModalController,
        private formBuilder: FormBuilder,
        private loadingService: LoadingService,
        private toastService: ToastService,
        private menuService: MenuService,
        private menuSelectionService: MenuSelectionService
    ) {
        super(modalController);
    }

    ngOnInit() {
        this.menu = this.menuService.currentValue;
        const ms = !this.menuSelection ? new MenuSelection() : this.menuSelection;
        if (!this.menuSelection) {
            ms.simpleSelect = true;
        }
        this.menuSelection = ms;
        this.selectedItems = this.menuSelection.options == null ? [] : [...this.menuSelection.options];
        this.prices = this.menuSelection.prices == null ? [] : [...this.menuSelection.prices];

        this.options = this.menu.menuItems.reduce((acc: any[], menuItem: MenuItem) => {
            const combo = [];
            if (!!menuItem.prices && menuItem.prices.length > 0) {
                menuItem.prices.forEach(menuItemPrice => {
                    combo.push({
                        menuItem,
                        menuItemPrice
                    });
                });
            } else {
                combo.push({menuItem, menuItemPrice: null});
            }
            acc = acc.concat(combo);
            return acc;
        }, []);

        if (this.prices.length === 0) {
            const price = new MenuSelectionPrice();
            price.price = 0;
            price.countMin = 1;
            this.prices = [price];
        }

        this.informationFormGroup = this.formBuilder.group({
            id: this.menuSelection.id,
            parentId: this.menu.id,
            name: [this.menuSelection.name, Validators.required],
            minCount: [this.menuSelection.minCount, this.menuSelection.required ? Validators.required : null],
            maxCount: this.menuSelection.maxCount,
            required: this.menuSelection.required,
            priority: this.menuSelection.priority,
            simpleSelect: this.menuSelection.simpleSelect,
            description: this.menuSelection.description
        });

        this.informationFormGroup.get('required').valueChanges.subscribe(v => {
            this.informationFormGroup.get('minCount').setValidators(v ? [Validators.required] : null);

            if (v && !this.informationFormGroup.get('minCount').value) {
                this.informationFormGroup.get('minCount').setValue(1);
            } else {
                this.informationFormGroup.get('minCount').setValue(null);
            }

            this.informationFormGroup.get('minCount').updateValueAndValidity();
        });

        this.subscribe(this.menuService.current.subscribe(m => {
            this.menu = !!m ? new Menu(m) : null;
        }));
    }

    changeMin(menuSelectionPrice: MenuSelectionPrice) {
        const index = this.prices.indexOf(menuSelectionPrice);
        if (index === 0) {
            menuSelectionPrice.countMin = 1;
        }

        if (menuSelectionPrice.countMin > menuSelectionPrice.countMax) {
            menuSelectionPrice.countMax = menuSelectionPrice.countMin;
            this.changeMax(menuSelectionPrice);
        }

        if (index - 1 >= 0) {
            const previous = this.prices[index - 1];
            if (previous.countMin >= menuSelectionPrice.countMin) {
                menuSelectionPrice.countMin = previous.countMin;
                this.removePrice(previous);
            } else {
                previous.countMax = menuSelectionPrice.countMin - 1;
                if (previous.countMax < previous.countMin) {
                    previous.countMin = previous.countMax;
                    this.changeMin(previous);
                }
            }
        }
    }

    changeMax(menuSelectionPrice: MenuSelectionPrice) {
        const index = this.prices.indexOf(menuSelectionPrice);
        if (menuSelectionPrice.countMax == null) {
            this.prices.splice(index + 1, this.prices.length - index);
        } else {
            if (menuSelectionPrice.countMax < menuSelectionPrice.countMin) {
                menuSelectionPrice.countMin = menuSelectionPrice.countMax;
                this.changeMin(menuSelectionPrice);
            }

            if (index + 1 < this.prices.length) {
                this.prices[index + 1].countMin = menuSelectionPrice.countMax + 1;
                if (this.prices[index + 1].countMin > this.prices[index + 1].countMax) {
                    this.prices[index + 1].countMax = this.prices[index + 1].countMin;
                    this.changeMax(this.prices[index + 1]);
                }
            }
        }
    }

    createPrice() {
        const lastPrice = this.prices[this.prices.length - 1];
        const price = new MenuSelectionPrice();
        price.countMin = lastPrice.countMax != null ? lastPrice.countMax + 1 : lastPrice.countMin + 1;
        if (lastPrice.countMax == null) {
            lastPrice.countMax = lastPrice.countMin;
        }

        price.countMax = null;
        this.prices.push(price);
    }

    removePrice(menuSelectionPrice: MenuSelectionPrice) {
        if (this.prices.length > 1) {
            const index = this.prices.indexOf(menuSelectionPrice);
            if (index === 0) {
                this.prices[index + 1].countMin = menuSelectionPrice.countMin;
            } else {
                this.prices[index - 1].countMax = menuSelectionPrice.countMax;
            }
            this.prices.splice(index, 1);
        }
    }

    filterByNameOrSize(items: MenuSelectionItem[], filter: string) {
        if (!!items && items.length > 1 && !!filter) {
            return items.filter(a => {
                return ((!!a.menuItem && a.menuItem.name.toLowerCase().includes(filter.toLowerCase())) ||
                    (!!a.menuItemPrice && a.menuItemPrice.name.toLowerCase().includes(filter.toLowerCase()))
                );
            });
        } else {
            return [...items];
        }
    }

    async openAddMenuItem() {
        const item = await this.addMenuItem(null);
        if (item != null) {
            const menuSelectionItem = new MenuSelectionItem();
            menuSelectionItem.menuItem = item;
            menuSelectionItem.printForDefault = true;
            if (!!item.prices && item.prices.length > 0) {
                menuSelectionItem.menuItemPrice = item.prices.find(p => p.defaultPrice);
                if (!menuSelectionItem.menuItemPrice) {
                    menuSelectionItem.menuItemPrice = item.prices[0];
                }
            }
            menuSelectionItem.sortOrder = this.selectedItems.length + 1;
            this.selectedItems = [...this.selectedItems, menuSelectionItem];
        }
    }

    removeItem(item: MenuSelectionItem) {
        this.selectedItems = this.selectedItems.filter(t => t.menuItem.id !== item.menuItem.id
            || t.menuItemPrice.id !== item.menuItemPrice.id);
    }

    markItems(items: any[], selectedItems: any[], selectionParam: string, disabledParam: string) {
        items.forEach(i => {
            i[selectionParam] = selectedItems.some(s => s.menuItem.id === i.menuItem.id
                && (
                    (!s.menuItemPrice && !i.menuItemPrice)
                    || (!!s.menuItemPrice && !!i.menuItemPrice && s.menuItemPrice.id === i.menuItemPrice.id)
                )
            );
        });
    }

    updateSelectedItems(items: any[]) {
        const update = this.selectedItems.filter(s =>
            items.some(i => s.menuItem.id === i.menuItem.id
                && (
                    (!s.menuItemPrice && !i.menuItemPrice)
                    || (!!s.menuItemPrice && !!i.menuItemPrice && s.menuItemPrice.id === i.menuItemPrice.id)
                )));

        items.filter(i => !update.some(s => s.menuItem.id === i.menuItem.id
            && (
                (!s.menuItemPrice && !i.menuItemPrice)
                || (!!s.menuItemPrice && !!i.menuItemPrice && s.menuItemPrice.id === i.menuItemPrice.id)
            ))).forEach(m => {
            m.printForDefault = true;
            update.push(m);
        });

        this.selectedItems = sortBySortOrder(update);
    }

    valid() {
        return this.informationFormGroup.valid && this.validPrices() && this.selectedItems.length > 0;
    }

    validPrices() {
        return this.prices.length > 0
            && this.prices.filter(s => !!s.price || s.price === 0).length === this.prices.length;
    }

    onSubmit() {
        this.loadingService.present();
        const selection: MenuSelection = new MenuSelection(this.informationFormGroup.value);
        selection.options = this.selectedItems;
        selection.prices = this.prices;

        if (selection.id != null) {
            this.menuSelectionService.update(selection).subscribe(
                (sel: MenuSelection) => {
                    this.loadingService.dismiss();
                    this.close(sel);
                }, (error) => this.errorHandler(error)
            );
        } else {
            this.menuSelectionService.create(selection).subscribe(
                (sel: MenuSelection) => {
                    this.loadingService.dismiss();
                    this.close(sel);
                }, (error) => this.errorHandler(error)
            );
        }
    }

    errorHandler(error) {
        this.loadingService.dismiss();
        if (error.status === 422) {
            this.toastService.error('The label is out of sync from a previous change. Please refresh to make changes.');
            this.close();
        } else if (error.status === 424) {
            this.toastService.error('Failed dependency.');
        } else if (error.status === 409) {
            this.toastService.error('The selection cannot contain any items that also include this selection.');
        } else {
            this.toastService.error('A server error occurred.');
        }
    }
}
